import { cn } from '@theprintbar/shared';
import { HTMLProps } from 'react';
import { useField } from 'remix-validated-form';
import { useOutsideForm } from '..';
import { useDragAndDrop } from './useDragAndDrop';

interface DragAndDropProps extends HTMLProps<HTMLInputElement> {
	label?: string;
	typeText?: string;
	labelClassName?: string;
	name: string;
	outsideForm?: boolean;
}

export function DragAndDropArea({ label, labelClassName, outsideForm, className, typeText, name, ...InputProps }: DragAndDropProps) {
	const { inputRef, dragOver, dropRef } = useDragAndDrop();
	const { error, getInputProps } = outsideForm
		? useOutsideForm(name || '')
		: useField(name || '', { validationBehavior: { initial: 'onChange' } });

	return (
		<>
			<div ref={dropRef}>
				{label && <label className={cn('block text-xs font-medium text-gray-700 ml-0.5 mb-1', labelClassName)}>{label}</label>}
				<button
					type="button"
					onClick={() => {
						if (inputRef.current) {
							inputRef.current.value = '';
							inputRef.current.click();
						}
					}}
					className={cn('group flex justify-center items-center border-2 border-gray-300 border-dashed rounded-lg', className)}
				>
					<div className="transition space-y-1 group-hover:scale-105 text-center">
						<svg
							className="mx-auto h-12 w-12 text-gray-400"
							stroke="currentColor"
							fill="none"
							viewBox="0 0 48 48"
							aria-hidden="true"
						>
							<path
								d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
								strokeWidth={2}
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<label className="relative cursor-pointer rounded-md font-medium text-gray-500 hover:text-gray-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand-500">
							{dragOver ? <span>Drop {typeText || 'File'}</span> : <span>Upload or drop a {typeText || 'File'}</span>}
						</label>
					</div>
					<input
						ref={inputRef}
						{...getInputProps(InputProps)}
						onChange={e => {
							const files = e.target.files;
							const accept = InputProps.accept;

							if (!files || !accept) return;

							const acceptTypes = accept.split(',').map(type => type.trim().toLowerCase());

							const isFileAccepted = (file: File) => {
								const fileType = file.type.toLowerCase();
								const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

								return (
									!!file.size &&
									acceptTypes.some(
										type =>
											type === '*' ||
											fileType === type ||
											(type.endsWith('/*') && fileType.startsWith(type.split('/')[0] + '/')) ||
											(type.startsWith('.') && fileExtension === type),
									)
								);
							};

							const invalidFiles = Array.from(files).filter(file => !isFileAccepted(file));

							if (invalidFiles.length > 0) {
								// Optionally log invalid file names:
								alert('Please only upload valid files');
								e.target.value = ''; // clear input
								return;
							}

							InputProps.onChange?.(e);
						}}
						type="file"
						className="hidden"
					/>
				</button>
				{!!error && (
					<span id={`${name}-error`} className="text-xs text-red-500">
						{error}
					</span>
				)}
			</div>
		</>
	);
}
